/**
 * Typed wrappers for the `/api/builder/admin/*` AI endpoints
 * (PR-4 builder reorg — Panorama IA):
 *
 *   - Issues triage           → `src/builder/api/routes/admin_issues.py`
 *   - AI Analysis snapshots   → `src/builder/api/routes/admin_ai_snapshots.py`
 *   - AI Context notes        → `src/builder/api/routes/admin_ai_notes.py`
 *   - Project-level training  → `src/builder/api/routes/projects.py`
 *
 * Types mirror `src/builder/schemas/admin_ai.py`. The compute actions
 * (regenerate / backfill / trigger / regenerate-affected) are PROXIED
 * by the builder to the portal runtime — they can answer:
 *
 *   - 503 `"Portal proxy not configured — set PORTAL_RECON_BASE_URL"`
 *     when the deployment lacks the proxy env, or a 503 naming
 *     `tenants/<slug>/portal/service_token` when the tenant has no
 *     service token in the secret store, or
 *   - 502 `{message, portal_status, portal_detail}` when the portal
 *     itself errored.
 *
 * Per Rule 1 those errors must surface VERBATIM in the UI — use
 * `formatAdminAiError()` to render them without swallowing detail.
 */

import { api, HttpError } from "@/lib/api";

// ───────────────────────────────────────────────────────────
// Issues
// ───────────────────────────────────────────────────────────

export interface IssueSummary {
  id: number;
  report_key: string;
  chat_context_key: string;
  client_key: string;
  session_id: string;
  turn_index: number;
  question: string;
  heuristic: string;
  heuristic_detail: string | null;
  status: string;
  resolution_action: string | null;
  created_at: string;
  updated_at: string;
}

export interface PaginatedIssues {
  items: IssueSummary[];
  total: number;
  page: number;
  page_size: number;
}

export interface IssueDetail extends IssueSummary {
  response: string | null;
  tool_calls: Array<Record<string, unknown>>;
  resolution_notes: string | null;
  resolved_by: string | null;
  resolved_at: string | null;
}

export interface IssueStatusCount {
  status: string;
  count: number;
}

export interface IssueHeuristicCount {
  heuristic: string;
  count: number;
}

export interface IssueReportCount {
  report_key: string;
  count: number;
}

export interface IssueWeeklyPoint {
  /** Monday of the ISO week, `YYYY-MM-DD`. */
  week_start: string;
  count: number;
}

export interface IssueMetrics {
  total: number;
  /** status IN (new, in_review) */
  open_count: number;
  resolved_count: number;
  dismissed_count: number;
  issues_this_week: number;
  by_status: IssueStatusCount[];
  by_heuristic: IssueHeuristicCount[];
  by_report: IssueReportCount[];
  /** Last 60 days. */
  weekly_series: IssueWeeklyPoint[];
  top_report: string | null;
  top_heuristic: string | null;
}

export type IssueResolveAction =
  | "promoted_to_example"
  | "added_doc"
  | "known_limitation"
  | "wont_fix";

export type IssueDismissReason =
  | "wont_fix"
  | "duplicate"
  | "known_limitation";

export interface IssueActionResponse {
  ok: boolean;
  issue: IssueDetail;
}

const ISSUES_BASE = "/api/builder/admin/issues";

export const adminIssuesApi = {
  list: (
    params: {
      report_key?: string;
      status?: string;
      heuristic?: string;
      since?: string;
      until?: string;
      page?: number;
      page_size?: number;
    } = {},
  ) => api.get<PaginatedIssues>(ISSUES_BASE, { query: params }),

  metrics: () => api.get<IssueMetrics>(`${ISSUES_BASE}/metrics`),

  detail: (id: number) => api.get<IssueDetail>(`${ISSUES_BASE}/${id}`),

  /**
   * For `action === "promoted_to_example"` both
   * `training_example_question` and `training_example_sql` are
   * required — the SQL is validated server-side (sql_guard) and a
   * `report_training` example row is inserted.
   */
  resolve: (
    id: number,
    body: {
      action: IssueResolveAction;
      notes?: string;
      training_example_question?: string;
      training_example_sql?: string;
    },
  ) => api.post<IssueActionResponse>(`${ISSUES_BASE}/${id}/resolve`, body),

  dismiss: (
    id: number,
    body: { reason: IssueDismissReason; notes?: string },
  ) => api.post<IssueActionResponse>(`${ISSUES_BASE}/${id}/dismiss`, body),

  reopen: (id: number) =>
    api.post<IssueActionResponse>(`${ISSUES_BASE}/${id}/reopen`),
};

// ───────────────────────────────────────────────────────────
// AI Analysis snapshots
// ───────────────────────────────────────────────────────────

export interface FeedbackTally {
  up: number;
  down: number;
  comments: number;
}

export interface AIAnalysisSnapshotListItem {
  id: string;
  as_of_date: string;
  week_iso: string;
  executive_summary_short: string;
  insights_count: number;
  feedback_summary: FeedbackTally;
  created_at: string;
  regenerated_count: number;
}

export interface AIAnalysisSnapshotCount {
  report_key: string;
  count: number;
}

export interface AIAnalysisInsight {
  title: string;
  summary: string;
  detail: string;
  /** 0-based position; `-1` is reserved for the executive summary. */
  index: number;
}

export interface AIAnalysisSnapshot {
  id: string;
  report_key: string;
  as_of_date: string;
  week_iso: string;
  filters: Record<string, unknown>;
  executive_summary: string;
  insights: AIAnalysisInsight[];
  model: string;
  regenerated_count: number;
  created_at: string;
  updated_at: string;
}

export interface PerInsightTally {
  index: number;
  title: string;
  up: number;
  down: number;
  comments: Array<{
    rating: string;
    comment: string;
    user_id: string | null;
    created_at: string;
  }>;
}

export interface InsightFeedbackSummary {
  snapshot_id: string;
  per_insight: PerInsightTally[];
  overall: FeedbackTally;
}

/** Portal passthrough for `POST .../backfill` (202). */
export interface BackfillStartResponse {
  job_id: string;
  status: string;
}

/** Portal passthrough for `GET .../backfill/{job_id}`. */
export interface BackfillStatusResponse {
  job_id: string;
  report_key?: string;
  status: string;
  processed: number;
  total: number;
  created: number;
  error: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

/** Portal passthrough for `POST /ai-snapshots/trigger`. */
export interface TriggerWatcherResponse {
  created: number;
}

const ADMIN_BASE = "/api/builder/admin";

const snapshotsBase = (reportKey: string): string =>
  `${ADMIN_BASE}/reports/${encodeURIComponent(reportKey)}/ai-snapshots`;

export const aiSnapshotsAdminApi = {
  count: (reportKey: string) =>
    api.get<AIAnalysisSnapshotCount>(`${snapshotsBase(reportKey)}/count`),

  list: (
    reportKey: string,
    params: { limit?: number; before?: string } = {},
  ) =>
    api.get<AIAnalysisSnapshotListItem[]>(snapshotsBase(reportKey), {
      query: params,
    }),

  get: (reportKey: string, snapshotId: string) =>
    api.get<AIAnalysisSnapshot>(
      `${snapshotsBase(reportKey)}/${encodeURIComponent(snapshotId)}`,
    ),

  feedbackSummary: (reportKey: string, snapshotId: string) =>
    api.get<InsightFeedbackSummary>(
      `${snapshotsBase(reportKey)}/${encodeURIComponent(snapshotId)}/feedback-summary`,
    ),

  /** PROXIED — full LLM re-render on the portal (can 502/503). */
  regenerate: (reportKey: string, snapshotId: string) =>
    api.post<AIAnalysisSnapshot>(
      `${snapshotsBase(reportKey)}/${encodeURIComponent(snapshotId)}/regenerate`,
    ),

  /** PROXIED — starts the portal background job (202, can 502/503). */
  startBackfill: (
    reportKey: string,
    body: { start?: string; end?: string } = {},
  ) =>
    api.post<BackfillStartResponse>(
      `${snapshotsBase(reportKey)}/backfill`,
      body,
    ),

  /** PROXIED — poll while `status === "running"` (can 502/503). */
  backfillStatus: (reportKey: string, jobId: string) =>
    api.get<BackfillStatusResponse>(
      `${snapshotsBase(reportKey)}/backfill/${encodeURIComponent(jobId)}`,
    ),

  /** PROXIED — force a snapshot-watcher pass on the portal. */
  triggerWatcher: () =>
    api.post<TriggerWatcherResponse>(`${ADMIN_BASE}/ai-snapshots/trigger`),
};

// ───────────────────────────────────────────────────────────
// AI Context notes
// ───────────────────────────────────────────────────────────

export interface AIContextNote {
  id: string;
  title: string;
  body: string;
  report_keys: string[];
  filters: Record<string, string>;
  date_start: string | null;
  date_end: string | null;
  reference_allowed: boolean;
  active: boolean;
  created_by: string | null;
  created_at: string;
  updated_at: string;
}

export interface AIContextNoteInput {
  title: string;
  body: string;
  report_keys: string[];
  filters: Record<string, string>;
  date_start: string | null;
  date_end: string | null;
  reference_allowed: boolean;
}

export interface AIContextNoteAffectedPreview {
  note_id: string;
  affected_count: number;
}

/** Portal passthrough for `POST /{id}/regenerate-affected` (202). */
export interface NoteRegenStartResponse {
  job_id: string;
  status: string;
}

/** Portal passthrough for `GET /{id}/regenerate-affected/{job_id}`. */
export interface NoteRegenStatusResponse {
  job_id: string;
  note_id?: string;
  status: string;
  processed: number;
  total: number;
  regenerated: number;
  error: string | null;
  started_at?: string | null;
  finished_at?: string | null;
}

const NOTES_BASE = "/api/builder/admin/ai-context-notes";

export const aiContextNotesAdminApi = {
  /**
   * `active` semantics mirror the route default: omitted → active
   * only (server default `true`); `false` → archived; `null` → BOTH
   * (sent as `?active=all` — an empty `?active=` is a 422, FastAPI
   * does not parse it as None for a query param).
   */
  list: (
    params: {
      active?: boolean | null;
      report_key?: string;
      q?: string;
      limit?: number;
    } = {},
  ) =>
    api.get<AIContextNote[]>(NOTES_BASE, {
      query: {
        active: params.active === null ? "all" : params.active,
        report_key: params.report_key,
        q: params.q,
        limit: params.limit,
      },
    }),

  get: (id: string) =>
    api.get<AIContextNote>(`${NOTES_BASE}/${encodeURIComponent(id)}`),

  create: (body: AIContextNoteInput) =>
    api.post<AIContextNote>(NOTES_BASE, body),

  update: (id: string, body: Partial<AIContextNoteInput> & { active?: boolean }) =>
    api.patch<AIContextNote>(`${NOTES_BASE}/${encodeURIComponent(id)}`, body),

  /** Soft delete — flips `active=false`, returns the archived note. */
  archive: (id: string) =>
    api.delete<AIContextNote>(`${NOTES_BASE}/${encodeURIComponent(id)}`),

  affectedPreview: (id: string) =>
    api.get<AIContextNoteAffectedPreview>(
      `${NOTES_BASE}/${encodeURIComponent(id)}/affected-snapshots`,
    ),

  /** PROXIED — starts the portal regen job (202, can 502/503). */
  startRegenerate: (id: string) =>
    api.post<NoteRegenStartResponse>(
      `${NOTES_BASE}/${encodeURIComponent(id)}/regenerate-affected`,
    ),

  /** PROXIED — poll while `status === "running"` (can 502/503). */
  regenerateStatus: (id: string, jobId: string) =>
    api.get<NoteRegenStatusResponse>(
      `${NOTES_BASE}/${encodeURIComponent(id)}/regenerate-affected/${encodeURIComponent(jobId)}`,
    ),
};

// ───────────────────────────────────────────────────────────
// Project-level training ("Conocimiento del proyecto")
// ───────────────────────────────────────────────────────────

export type ProjectTrainingType = "column_docs" | "synonyms" | "example";

export const PROJECT_TRAINING_TYPES: readonly ProjectTrainingType[] = [
  "column_docs",
  "synonyms",
  "example",
];

export interface ProjectTrainingRow {
  id: string;
  project_id: string;
  training_type: ProjectTrainingType;
  name: string;
  content: string;
  position: number;
}

export interface ProjectTrainingInput {
  training_type: ProjectTrainingType;
  name: string;
  content: string;
  position: number;
}

const projectTrainingBase = (slug: string): string =>
  `/api/builder/projects/${encodeURIComponent(slug)}/training`;

export const projectTrainingApi = {
  /** All rows of the project, ordered by (training_type, position). */
  list: (slug: string) =>
    api.get<ProjectTrainingRow[]>(projectTrainingBase(slug)),

  create: (slug: string, body: ProjectTrainingInput) =>
    api.post<ProjectTrainingRow>(projectTrainingBase(slug), body),

  update: (slug: string, id: string, body: ProjectTrainingInput) =>
    api.put<ProjectTrainingRow>(
      `${projectTrainingBase(slug)}/${encodeURIComponent(id)}`,
      body,
    ),

  remove: (slug: string, id: string) =>
    api.delete<void>(
      `${projectTrainingBase(slug)}/${encodeURIComponent(id)}`,
    ),
};

// ───────────────────────────────────────────────────────────
// Error formatting — proxied 502/503 surfaced VERBATIM
// ───────────────────────────────────────────────────────────

/**
 * Render an error for the UI without losing the portal detail.
 *
 * - 503 "Portal proxy not configured…" → already the HttpError message.
 * - 502 `{detail: {message, portal_status, portal_detail}}` → all three
 *   fields concatenated so the admin sees exactly what the portal said.
 * - Anything else → its message.
 */
export function formatAdminAiError(err: unknown): string {
  if (err instanceof HttpError) {
    const payload = err.detail;
    if (payload && typeof payload === "object") {
      const detail = (payload as { detail?: unknown }).detail;
      if (detail && typeof detail === "object") {
        const d = detail as {
          message?: unknown;
          portal_status?: unknown;
          portal_detail?: unknown;
        };
        if (d.portal_status !== undefined || d.portal_detail !== undefined) {
          const portalDetail =
            typeof d.portal_detail === "string"
              ? d.portal_detail
              : JSON.stringify(d.portal_detail);
          return `${String(d.message ?? "Portal proxy error")} — portal ${String(
            d.portal_status ?? "?",
          )}: ${portalDetail}`;
        }
      }
    }
    return err.message;
  }
  return err instanceof Error ? err.message : String(err);
}

// ───────────────────────────────────────────────────────────
// Shared date helpers for the Panorama surfaces
// ───────────────────────────────────────────────────────────

/**
 * Parse a server timestamp as UTC. The backend stores naive-UTC
 * datetimes (no tz suffix); without forcing `Z` the browser reads them
 * as local time, shifting by the user's offset.
 */
export function parseAsUTC(iso: string | null): Date | null {
  if (!iso) return null;
  const hasTz = /[Zz]$|[+-]\d{2}:?\d{2}$/.test(iso);
  const d = new Date(hasTz ? iso : iso + "Z");
  return Number.isNaN(d.getTime()) ? null : d;
}

/** Absolute local datetime, or em-dash. */
export function formatDateTime(iso: string | null): string {
  const d = parseAsUTC(iso);
  return d ? d.toLocaleString() : "—";
}

/**
 * Relative "3 h ago" style formatting via Intl. Falls through to the
 * absolute datetime for anything older than ~30 days (relative stops
 * being informative there).
 */
export function formatRelativeTime(iso: string | null, locale: string): string {
  const d = parseAsUTC(iso);
  if (!d) return "—";
  const deltaMs = d.getTime() - Date.now();
  const abs = Math.abs(deltaMs);
  const rtf = new Intl.RelativeTimeFormat(locale, { numeric: "auto" });
  const MIN = 60_000;
  const HOUR = 3_600_000;
  const DAY = 86_400_000;
  if (abs < MIN) return rtf.format(Math.round(deltaMs / 1000), "second");
  if (abs < HOUR) return rtf.format(Math.round(deltaMs / MIN), "minute");
  if (abs < DAY) return rtf.format(Math.round(deltaMs / HOUR), "hour");
  if (abs < 30 * DAY) return rtf.format(Math.round(deltaMs / DAY), "day");
  return d.toLocaleDateString();
}

/** Truncate with an ellipsis. */
export function truncateText(text: string, max: number): string {
  if (text.length <= max) return text;
  return text.slice(0, max - 1) + "…";
}
